import { Model } from "../mvvm/Model.js";
import { ServiceCatalog } from "./ServiceCatalog.js";
import { HOSTING_PLAN_INCLUDED_API_REQUESTS, HOSTING_PLAN_MONTHLY_CENTS, HOSTING_PLAN_YEARLY_CENTS, METERED_OVERAGE_CENTS_PER_1K_REQUESTS, } from "../constants/hostingBilling.js";
import { formatUsd } from "../formatUsd.js";
const MARKETING_NOTE = {
    foundation: "Your own domain, DNS, and email. No Lumora API — for brochure sites only.",
    payg: "Hosting only. No custom domain. You pay for API use as it happens.",
    starter: "Hosting, your domain, and email, with a monthly API allowance.",
    growth: "More traffic room, and the plan required for most extras.",
    pro: "Highest traffic room, custom data shapes, and outside connections.",
};
/** Plain-language bullets for the public services page only. */
const MARKETING_INCLUDES = {
    foundation: [
        "Your custom domain",
        "DNS and email setup help",
        "Site hosting for brochure pages",
    ],
    payg: [
        "No monthly hosting fee",
        "A Lumora web address",
        "Pay for API use as you go",
        "Security updates included",
    ],
    starter: [
        "Your domain and email",
        "75,000 API requests per month included",
        "Extra requests billed by use",
        "One small content update per month",
    ],
    growth: [
        "Everything in Starter",
        "Higher monthly API allowance",
        "Room for Growth extras (loyalty, delivery, and more)",
    ],
    pro: [
        "Everything in Growth",
        "Largest monthly API allowance",
        "Custom data shapes and outside connections",
    ],
};
const ORDER_CUT = {
    payg: "7% platform fee on online orders through our checkout",
    starter: "9% platform fee on online orders through our checkout",
    growth: "7% platform fee on online orders through our checkout",
    pro: "5% platform fee on online orders through our checkout",
};
const overagePer1k = formatUsd(METERED_OVERAGE_CENTS_PER_1K_REQUESTS).slice(1);
const starterIncludedLabel = HOSTING_PLAN_INCLUDED_API_REQUESTS.starter.toLocaleString("en-US");
const growthIncludedLabel = HOSTING_PLAN_INCLUDED_API_REQUESTS.growth.toLocaleString("en-US");
const proIncludedLabel = HOSTING_PLAN_INCLUDED_API_REQUESTS.pro.toLocaleString("en-US");
const PLANS = [
    {
        key: "foundation",
        name: "Foundation",
        monthlyCents: 0,
        yearlyCents: HOSTING_PLAN_YEARLY_CENTS.foundation,
        billingInterval: "year",
        blurb: "Edge hosting with custom domain — for Spark or Brand sites with no backend API.",
        recommended: false,
        includes: [
            "Secure edge-network hosting",
            "Custom domain connection (done-for-you DNS or DIY)",
            "Uptime monitoring",
        ],
        excludes: [
            "Shared Lumora API / checkout",
            "Included content updates (billed as work or retainer)",
        ],
    },
    {
        key: "payg",
        name: "Pay as you go",
        monthlyCents: HOSTING_PLAN_MONTHLY_CENTS.payg,
        yearlyCents: 0,
        billingInterval: "month",
        blurb: "$0/mo base — metered compute at ~pass-through cost. Pairs with Connect builds.",
        recommended: false,
        includes: [
            "$0 monthly hosting fee",
            "Lumora subdomain",
            "Pay only for API requests / compute (~pass-through cost)",
            "Shared backend, database, and security updates",
        ],
        excludes: [
            `Included request allotment (Starter includes ${starterIncludedLabel}/mo)`,
            "Custom domain (Growth or Pro)",
            "Fixed maintenance retainer",
        ],
    },
    {
        key: "starter",
        name: "Starter",
        monthlyCents: HOSTING_PLAN_MONTHLY_CENTS.starter,
        yearlyCents: 0,
        billingInterval: "month",
        blurb: "Lumora subdomain, included API allotment, and light maintenance.",
        recommended: true,
        includes: [
            "Lumora subdomain (custom domain requires Growth or Pro)",
            `${starterIncludedLabel} API requests/month included`,
            `Overage billed at ~$${overagePer1k} per 1,000 requests`,
            "1 minor content update/month (text/image swaps, menu price updates)",
            "Basic monthly traffic reporting",
        ],
        excludes: ["Custom domain", "Priority support SLA"],
    },
    {
        key: "growth",
        name: "Growth",
        monthlyCents: HOSTING_PLAN_MONTHLY_CENTS.growth,
        yearlyCents: 0,
        billingInterval: "month",
        blurb: "Custom domain, higher traffic allowance, and the sweet spot for growing businesses.",
        recommended: false,
        includes: [
            "Everything in Starter",
            "Custom domain with automated SSL",
            `${growthIncludedLabel} API requests/month included`,
            "Lower overage rates on extra usage",
            "Email DNS guidance for your domain",
        ],
        excludes: [
            "Dedicated support channel",
        ],
    },
    {
        key: "pro",
        name: "Pro",
        monthlyCents: HOSTING_PLAN_MONTHLY_CENTS.pro,
        yearlyCents: 0,
        billingInterval: "month",
        blurb: "High traffic, priority support, and the largest included usage pool.",
        recommended: false,
        includes: [
            "Everything in Growth",
            `${proIncludedLabel} API requests/month included`,
            "Priority support response",
            "Best overage rates on metered usage",
        ],
        excludes: [],
    },
];
/**
 * PlanCatalog is a static OOP model that holds the hosting plan catalog.
 * It is framework-agnostic — no React, no side effects.
 */
export class PlanCatalog extends Model {
    static plans = PLANS;
    static byKey(key) {
        const plan = this.plans.find((p) => p.key === key);
        if (!plan)
            throw new Error(`Unknown plan key: ${key}`);
        return plan;
    }
    static formatMonthly(cents) {
        return `${formatUsd(cents)}/mo`;
    }
    /** Human price line from plan amounts (API catalog or bootstrap fallback). */
    static formatPlanPrice(plan) {
        if (plan.key === "payg") {
            const meter = plan.meterCentsPer1k ?? METERED_OVERAGE_CENTS_PER_1K_REQUESTS;
            const per1k = formatUsd(meter).slice(1);
            return `$0 + ~$${per1k}/1k`;
        }
        if (plan.billingInterval === "year")
            return `${formatUsd(plan.yearlyCents)}/yr`;
        return PlanCatalog.formatMonthly(plan.monthlyCents);
    }
    /** Public services page — same list amounts as subscribe (#744). */
    static marketingPrice(plan) {
        return PlanCatalog.formatPlanPrice(plan);
    }
    static marketingNote(key) {
        return MARKETING_NOTE[key] ?? "";
    }
    static marketingIncludes(plan) {
        return MARKETING_INCLUDES[plan.key] ?? plan.includes;
    }
    /** Platform fee line for services hosting cards. Foundation has none. */
    static orderCutForPlan(key) {
        return ORDER_CUT[key] ?? null;
    }
    static marketingCards() {
        return PlanCatalog.plans.map((plan) => ({
            key: plan.key,
            name: plan.name,
            priceLabel: PlanCatalog.marketingPrice(plan),
            note: PlanCatalog.marketingNote(plan.key),
        }));
    }
    /** Starter / PAYG / Growth / Pro = 9 / 7 / 7 / 5. */
    static orderCutLine() {
        return "Order cut: Starter 9% / PAYG 7% / Growth 7% / Pro 5%";
    }
    static toQuoteOption(plan) {
        return {
            key: plan.key,
            name: plan.name,
            priceLabel: PlanCatalog.formatPlanPrice(plan),
            blurb: plan.blurb,
            includes: plan.includes,
            excludes: plan.excludes,
        };
    }
    /** GitHub Pages — default free static hosting for Spark (not a subscribe tier). */
    static githubPagesOption() {
        return {
            key: "github_pages",
            name: "GitHub Pages (Spark default)",
            priceLabel: "$0",
            blurb: "Free static hosting on GitHub Pages or a Lumora subdomain — no custom domain in the Spark build.",
            includes: [
                "Static site hosting on GitHub Pages or Lumora subdomain",
                "Best-effort courtesy hosting (no uptime SLA)",
            ],
            excludes: [
                "Custom domain wired by Lumora (see Brand or Foundation)",
                "Shared Lumora API / checkout",
            ],
        };
    }
    /** Static hosting ladder — Spark / Brand brochure sites (no backend). */
    static staticHostingOptions() {
        return [PlanCatalog.githubPagesOption(), PlanCatalog.toQuoteOption(PlanCatalog.byKey("foundation"))];
    }
    /** Backend hosting ladder — Connect / Commerce / Custom (Foundation does not apply). */
    static backendHostingPlans() {
        return ["payg", "starter", "growth", "pro"].map((k) => PlanCatalog.byKey(k));
    }
    /** Hosting choices for the marketing pricing page (#170) — one ladder per service tier. */
    static hostingOptionsForService(service) {
        if (ServiceCatalog.usesStaticHosting(service)) {
            return PlanCatalog.staticHostingOptions();
        }
        return PlanCatalog.backendHostingPlans().map((p) => PlanCatalog.toQuoteOption(p));
    }
    static isPackageValidForService(service, pkg) {
        return PlanCatalog.hostingOptionsForService(service).some((o) => o.key === pkg);
    }
    /**
     * Builds a plan list from backend DTOs (the DB-backed catalog), ordered by
     * `sort`. The DB stores descriptive metadata only, so price/recommended are
     * merged from the static defaults by key (falling back to 0/false for keys
     * the platform doesn't predefine). This keeps the DB the source of truth for
     * naming/ordering while preserving the existing pricing UX.
     */
    static fromDTOs(dtos) {
        return [...dtos]
            .sort((a, b) => a.sort - b.sort)
            .map((dto) => {
            const fallback = PLANS.find((p) => p.key === dto.key);
            const interval = dto.billingInterval ?? fallback?.billingInterval ?? "month";
            const monthlyCents = interval === "year"
                ? 0
                : (dto.amountCents ?? fallback?.monthlyCents ?? HOSTING_PLAN_MONTHLY_CENTS.payg);
            const yearlyCents = interval === "year" ? (dto.amountCents ?? fallback?.yearlyCents ?? 0) : (fallback?.yearlyCents ?? 0);
            const meterCentsPer1k = dto.meterCentsPer1k ?? fallback?.meterCentsPer1k;
            return {
                key: dto.key,
                name: dto.name,
                blurb: dto.description,
                monthlyCents,
                yearlyCents,
                billingInterval: interval,
                recommended: fallback?.recommended ?? false,
                includes: fallback?.includes ?? [],
                excludes: fallback?.excludes ?? [],
                ...(meterCentsPer1k != null ? { meterCentsPer1k } : {}),
            };
        });
    }
    validate() {
        const errors = [];
        if (PlanCatalog.plans.length === 0)
            errors.push("Plan catalog is empty.");
        for (const plan of PlanCatalog.plans) {
            if (plan.includes.length === 0)
                errors.push(`Plan ${plan.key} has no includes.`);
        }
        return errors;
    }
}
//# sourceMappingURL=PlanCatalog.js.map